import { useState, useMemo } from "react";
import {
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  InputAdornment,
  Divider,
  Snackbar,
  Alert,
  Icon,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";

import "../../Styles/form.css";
import "../../Styles/general.css";
import { useEffect } from "react";
import { useError } from "../../Context/errorContext";
import MyHoursCompare from "../myHoursCompare";
import JoosepCompare from "./joosepCompare";
import { statuses } from "../helpers";

/**
 * A more polished, accessible, and compact code editor/display for a project code string.
 *
 * Notes:
 * - Keeps your original props API intact for drop-in replacement.
 * - Adds copy-to-clipboard, subtle layout, keyboard shortcuts, and nicer visuals.
 * - Press Enter to save while editing. Esc will exit edit mode (without reverting).
 */
const ProjectCodeBlock = ({
  fixedCode,
  serialNumber,
  handleChange,
  editCode,
  setEditCode,
  handleCodeUpdate,
  handleCodeValidation,
  handleCodeGeneration,
  handleCodeCancel,
  similarProjects,
  project,
  moreLabel = "Show Similar Projects",
  lessLabel = "Hide Similar Projects",
  isCodeValid,
  validating,
  decisionForm,
  syncToMyHours,
  setSyncToMyHours,
  syncToJoosep,
  setSyncToJoosep,
}) => {
  const [copiedOpen, setCopiedOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { setAlert } = useError();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const helperText = useMemo(() => {
    return (fixedCode + serialNumber).length !== 10
      ? "Code must be of lenght 10!"
      : "Code must be unique!";
  }, [fixedCode, serialNumber]);
  const invalidCode = Boolean(!validating && !isCodeValid);

  const label = useMemo(
    () => (editCode ? "Edit project code" : "Project code"),
    [editCode],
  );

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fixedCode + serialNumber || "");
      setCopiedOpen(true);
    } catch (_) {
      // noop – clipboard might be blocked; we silently ignore
    }
  };

  const onReset = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await Promise.resolve(handleCodeGeneration(project.uid));
      setEditCode(true);
    } finally {
      setSaving(false);
    }
  };

  const onSave = async () => {
    if (saving) return;
    setConfirmDialogOpen(true);
  };

  const onSaveConfirm = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await Promise.resolve(handleCodeUpdate());
      setEditCode(false);
    } finally {
      setSaving(false);
      setConfirmDialogOpen(false);
    }
  };

  const onCancel = () => {
    handleCodeCancel();
    setEditCode(false);
    setSaving(false);
  };

  useEffect(() => {
    handleCodeValidation();
  }, [serialNumber]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: (t) =>
          editCode ? t.palette.background.paper : t.palette.action.hover,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        mb={1.5}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {!editCode && <LockRoundedIcon fontSize="small" aria-hidden />}
          <Typography variant="subtitle1" fontWeight={600}>
            Code
          </Typography>
        </Stack>

        {editCode ? (
          <Stack direction="row" spacing={1}>
            <Button
              onClick={onReset}
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              Reset
            </Button>
            {!decisionForm && (
              <Button
                onClick={() => {
                  onSave();
                  setSyncToMyHours(true);
                }}
                variant="contained"
                startIcon={<SaveRoundedIcon />}
                disabled={saving || invalidCode}
                sx={{ borderRadius: 2 }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            )}
            {!decisionForm && (
              <Button
                onClick={onCancel}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
            )}
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              onClick={onReset}
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              Reset
            </Button>
            <Button
              onClick={() => setEditCode(true)}
              variant="contained"
              startIcon={<EditRoundedIcon />}
              sx={{ borderRadius: 2 }}
            >
              Edit
            </Button>
          </Stack>
        )}
      </Stack>

      <Box>
        {editCode ? (
          <TextField
            id="code"
            label={label}
            placeholder="Project code"
            variant="outlined"
            value={serialNumber || ""}
            onChange={handleChange}
            error={invalidCode}
            helperText={invalidCode ? helperText : ""}
            disabled={!editCode}
            fullWidth
            size="small"
            onKeyDown={(e) => {
              if (editCode && e.key === "Enter") onSave();
              if (editCode && e.key === "Escape") setEditCode(false);
            }}
            InputProps={{
              readOnly: !editCode,
              sx: {
                fontFamily:
                  '"Fira Code", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                letterSpacing: 0.2,
              },
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {(fixedCode + serialNumber).length !== 10 && (
                      <Tooltip
                        title="Code must be of length 10"
                        placement="top"
                      >
                        <Typography
                          component="div"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            borderColor: "divider",
                            p: "6px",
                            color: "red",
                            fontWeight: "bold",
                            fontFamily:
                              '"Fira Code", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            letterSpacing: 0.2,
                          }}
                        >
                          <Box
                            sx={{ flex: 1, userSelect: "text", cursor: "text" }}
                          >
                            {(fixedCode + serialNumber).length}
                          </Box>
                        </Typography>
                      </Tooltip>
                    )}
                    <Tooltip title="Code validity" placement="top">
                      {/* span keeps Tooltip working if the child becomes disabled */}
                      <span>
                        {/* Use IconButton for consistent sizing/alignment in adornments */}
                        <IconButton
                          size="small"
                          aria-label="Validation status"
                          disableRipple
                          disableFocusRipple
                          disabled
                          sx={{ p: 0.25 }} // tight like Typography inline icon
                        >
                          {validating ? (
                            <HourglassBottomRounded
                              fontSize="small"
                              color="warning"
                            />
                          ) : isCodeValid ? (
                            <CheckCircleRounded
                              fontSize="small"
                              color="success"
                            />
                          ) : (
                            <ErrorIcon fontSize="small" color="error" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Copy" placement="top">
                      <span>
                        <IconButton
                          onMouseDown={(e) => e.preventDefault()} // avoid selecting / stealing focus
                          onClick={onCopy}
                          size="small"
                          aria-label="Copy code"
                          edge="end"
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </InputAdornment>
              ),
              startAdornment: <Box sx={{ color: "grey" }}>{fixedCode}</Box>,
            }}
          />
        ) : (
          <Typography
            component="div"
            sx={{
              display: "flex",
              alignItems: "center",
              p: "6px 8px",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              color: "gray",
              fontFamily:
                '"Fira Code", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              letterSpacing: 0.2,
            }}
          >
            <Box sx={{ flex: 1, userSelect: "text", cursor: "text" }}>
              {fixedCode}
              {serialNumber}
            </Box>

            <Tooltip title="Valid" placement="top">
              {/* span keeps Tooltip working if IconButton is ever disabled */}
              <span>
                <Icon size="small" aria-label="Copy code">
                  {validating || (fixedCode + serialNumber).length !== 10 ? (
                    <HourglassBottomRounded fontSize="small" color="warning" />
                  ) : isCodeValid ? (
                    <CheckCircleRounded fontSize="small" color="success" />
                  ) : (
                    <ErrorIcon fontSize="small" color="error" />
                  )}
                </Icon>
              </span>
            </Tooltip>
            <Tooltip title="Copy" placement="top">
              {/* span keeps Tooltip working if IconButton is ever disabled */}
              <span>
                <IconButton
                  onMouseDown={(e) => e.preventDefault()} // avoid selecting text
                  onClick={onCopy}
                  size="small"
                  aria-label="Copy code"
                  sx={{ ml: 1 }}
                >
                  <ContentCopyRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Typography>
        )}

        {/* Optional divider for visual separation when there is helper text */}
        {invalidCode && <Divider sx={{ mt: 1.5 }} />}
      </Box>

      {expanded &&
        similarProjects &&
        similarProjects
          .filter((s) => s.project_id !== project.project_id)
          .map((p, i) => (
            <Stack
              key={i}
              direction="row"
              justifyContent="start"
              alignItems="center"
              sx={{ py: 0.75, gap: 2 }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  width: 150,
                  flexShrink: 0,
                  wordBreak: "break-word",
                  fontWeight: "bold",
                }}
              >
                {p.name}:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ml: 0,
                  wordBreak: "break-word",
                  flex: "0 1 auto",
                  minWidth: 0,
                  color:
                    p.Status.status_id >= statuses.CONFIRMED ? "green" : "red",
                }}
              >
                {p.code}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  wordBreak: "break-word",
                  flex: "0 1 auto",
                  minWidth: 0,
                }}
              >
                {p.Status.status_name}
              </Typography>
            </Stack>
          ))}

      {/* Minimal grey control below */}
      {
        <Box
          role="button"
          aria-expanded={expanded}
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && setExpanded((v) => !v)
          }
          sx={{
            mt: 0.5,
            color: (t) => t.palette.text.secondary,
            cursor: "pointer",
            userSelect: "none",
            display: "inline-block",
            textDecoration: "underline",
          }}
        >
          {expanded ? lessLabel : moreLabel}
        </Box>
      }

      <Snackbar
        open={copiedOpen}
        autoHideDuration={1800}
        onClose={() => setCopiedOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          elevation={0}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          Code copied to clipboard
        </Alert>
      </Snackbar>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="body"
        PaperProps={{
          sx: {
            maxHeight: "70vh", // limits dialog height
          },
        }}
      >
        <DialogContent>
          <MyHoursCompare
            newCode={fixedCode + serialNumber}
            project={project}
            syncToMyHours={syncToMyHours}
            setSyncToMyHours={setSyncToMyHours}
            setAlert={setAlert}
          ></MyHoursCompare>

          <Divider sx={{ py: 1, my: 2 }}></Divider>

          <JoosepCompare
            newCode={fixedCode + serialNumber}
            project={project}
            syncToJoosep={syncToJoosep}
            setSyncToJoosep={setSyncToJoosep}
            setAlert={setAlert}
          ></JoosepCompare>
        </DialogContent>
        <DialogActions sx={{ gap: 1.5 }}>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setSyncToMyHours(true);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={onSaveConfirm}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving
              ? "Saving…"
              : syncToMyHours || syncToJoosep
                ? "Save and sync"
                : "Save without sync"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProjectCodeBlock;
